import { useEffect, useState } from 'react'

import { useToast } from 'src/shared/hooks/use-toast'
import { cn } from 'src/shared/utils/cn'

import FileUploadBox from '../../components/file-upload-box'
import WarningIcon from '../../components/warning-icon'
import { FOOTER_DISCLAIMER_TEXT } from '../../constants/ul-copy'
import { useUploadLeadsContext } from '../../context/upload-leads-context'
import { type UploadedFileMeta, useUploadLeadsStore } from '../../store/upload-leads-store'

export default function ReuploadLeadsStep() {
  // const { uploadedFile: initialUploadedFile } = useUploadLeadsStore((state) => state.formData)
  const { toast } = useToast()
  const [reuploadFile, setReuploadFile] = useState<UploadedFileMeta | null>(null)
  const setFormData = useUploadLeadsStore((state) => state.setFormData)
  const { handleNext } = useUploadLeadsContext()
  const { setFooterConfig } = useUploadLeadsContext()
  const [requestRemoveFileToken, setRequestRemoveFileToken] = useState(1)
  const isUploaded = Boolean(reuploadFile?.previewData)

  const handleUploadedFileChange = (fileMeta: UploadedFileMeta | null) => {
    const { formData } = useUploadLeadsStore.getState()

    if (!fileMeta) {
      setReuploadFile(null)
      return
    }

    // 1. Check if uploaded file has a closed date column.

    if (fileMeta.previewData.closedAtColumnExists === false) {
      toast({
        title: 'Upload failed',
        description: 'Please upload a file that includes a "ClosedAt" column.',
        variant: 'destructive',
        duration: 8000,
      })
      setRequestRemoveFileToken(requestRemoveFileToken + 1)
      return
    }

    setReuploadFile(fileMeta)
    setFormData({
      ...formData,
      uploadedFile: fileMeta,
      uploadedFileHasClosedDate: fileMeta?.previewData.closedAtColumnExists ?? false,
    })
  }

  const handleNevermindClick = () => {
    const { formData } = useUploadLeadsStore.getState()
    setFormData({ ...formData, closedDeals: false })
    handleNext()
  }

  useEffect(() => {
    setFooterConfig({
      isPrimaryDisabled: !reuploadFile?.previewData,
      bottomLeftContent: {
        variant: 'icon-text',
        text: FOOTER_DISCLAIMER_TEXT,
        icon: <WarningIcon />,
      },
    })

    return () => {
      setFooterConfig(null)
    }
  }, [setFooterConfig, reuploadFile])

  return (
    <div className={cn('flex size-full items-center justify-center px-5')}>
      <div className="mx-auto w-full max-w-[500px]">
        <div className="flex flex-col items-center">
          <div className="w-full max-w-[460px] text-center">
            <h1 className="text-[36px] font-semibold leading-tight tracking-[-0.36px] text-primary">
              {isUploaded ? 'File uploaded' : 'This upload needs one extra column'}
            </h1>
            <p className="mt-2 text-[18px] font-medium leading-[1.6] text-muted">
              Re-upload your sheet with a <span className="text-primary">&ldquo;ClosedAt&rdquo;</span> column added
            </p>
          </div>

          <FileUploadBox
            initialFile={reuploadFile}
            onFileChange={handleUploadedFileChange}
            className="mt-10"
            requestRemoveFileToken={requestRemoveFileToken}
          />

          <button
            type="button"
            className="mt-5 text-center text-[16px] font-medium text-primary underline underline-offset-[3px]"
            onClick={handleNevermindClick}
          >
            Nevermind, it&apos;s not past clients
          </button>
        </div>
      </div>
    </div>
  )
}
